
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { GoogleGenAI, Type, Modality } from "@google/genai";
import { CityStats, Grid, AIGoal, NewsItem, BuildingType } from "../types";

const apiKey =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_GEMINI_API_KEY) ||
  process.env.API_KEY;

// Only initialize AI if API key is available
let ai: GoogleGenAI | null = null;
try {
  if (apiKey && apiKey !== 'your-api-key-here') {
    ai = new GoogleGenAI({ apiKey });
  }
} catch (error) {
  console.warn('AI service disabled - no valid API key provided');
  ai = null;
}

export const isGeminiConfigured = (): boolean => ai !== null;

// Models configuration
const FAST_MODEL = 'gemini-2.5-flash';
const COMPLEX_MODEL = 'gemini-3-pro-preview';
const TTS_MODEL = 'gemini-2.5-flash-preview-tts';

const SYSTEM_INSTRUCTION = "You are the AI Risk Officer (ARO) for a futuristic fintech city. Tone: Professional, urgent, slightly dystopian, corporate-cyberpunk. Keep responses concise.";

// Fallback functions when AI is not available
const fallbackNewsEvents = [
  { text: "Market volatility detected across financial sectors.", type: "neutral" as const },
  { text: "New regulatory guidelines issued by oversight committee.", type: "neutral" as const },
  { text: "Quarterly compliance audit scheduled for next week.", type: "neutral" as const },
  { text: "Cybersecurity awareness month begins with training initiatives.", type: "positive" as const },
  { text: "Systems maintenance scheduled for this weekend.", type: "neutral" as const }
];

const fallbackGoals = [
  { description: "Expand infrastructure to support growing operations.", priority: 1 },
  { description: "Implement additional security measures.", priority: 2 },
  { description: "Increase compliance monitoring capabilities.", priority: 1 },
  { description: "Optimize revenue generation systems.", priority: 2 }
];

export const generateNewsEvent = async (stats: CityStats, context: any): Promise<NewsItem | null> => {
    // Return fallback if AI is not available
    if (!ai) {
        const randomEvent = fallbackNewsEvents[Math.floor(Math.random() * fallbackNewsEvents.length)];
        return {
            id: Date.now().toString(),
            text: randomEvent.text,
            type: randomEvent.type
        };
    }

    try {
        const prompt = `Generate a single short news ticker headline (max 10 words) for the city.
        Current Stats:
        - Budget: $${stats.budget}
        - Compliance: ${stats.compliance}%
        - Threat Level: ${stats.threat}%

        The headline should relate to banking, cyber-security, or regulatory news.
        Format: JSON with 'text' and 'type' (positive/negative/neutral).`;

        const response = await ai.models.generateContent({
            model: FAST_MODEL,
            contents: prompt,
            config: {
                systemInstruction: SYSTEM_INSTRUCTION,
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        text: { type: Type.STRING },
                        type: { type: Type.STRING, enum: ["positive", "negative", "neutral"] }
                    }
                }
            }
        });

        if (response.text) {
            const data = JSON.parse(response.text);
            return {
                id: Date.now().toString(),
                text: data.text,
                type: data.type || 'neutral'
            };
        }
    } catch (e) {
        console.error("Gemini News Error:", e);
    }
    return null;
};

export const generateCityGoal = async (stats: CityStats, grid: Grid): Promise<AIGoal | null> => {
    // Return fallback if AI is not available
    if (!ai) {
        const randomGoal = fallbackGoals[Math.floor(Math.random() * fallbackGoals.length)];
        return {
            id: Date.now().toString(),
            description: randomGoal.description,
            priority: randomGoal.priority
        };
    }

    try {
        const buildingCounts: Record<string, number> = {};
        grid.flat().forEach(t => {
            if (t.buildingType !== 'None') {
                buildingCounts[t.buildingType] = (buildingCounts[t.buildingType] || 0) + 1;
            }
        });

        const prompt = `Analyze the city state and generate a strategic mandate (goal) for the architect.
        Stats: Budget $${stats.budget}, Compliance ${stats.compliance}%, Threat ${stats.threat}%.
        Buildings: ${JSON.stringify(buildingCounts)}
        
        Goal should be challenging but achievable.
        Return JSON.`;

        const response = await ai.models.generateContent({
            model: COMPLEX_MODEL,
            contents: prompt,
            config: {
                systemInstruction: SYSTEM_INSTRUCTION,
                responseMimeType: "application/json",
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        description: { type: Type.STRING, description: "Short command like 'Build 3 Firewalls'" },
                        targetType: { type: Type.STRING, enum: ["compliance", "budget", "building_count"] },
                        targetValue: { type: Type.NUMBER },
                        buildingType: { type: Type.STRING, description: "BuildingType enum value if targetType is building_count" },
                        reward: { type: Type.NUMBER }
                    }
                }
            }
        });
        
        if (response.text) {
            const data = JSON.parse(response.text);
            let bType: BuildingType | undefined = undefined;
            if (data.targetType === 'building_count' && data.buildingType) {
                 bType = data.buildingType as BuildingType;
            }

            return {
                description: data.description,
                targetType: data.targetType,
                targetValue: data.targetValue,
                buildingType: bType,
                reward: data.reward,
                completed: false
            };
        }

    } catch (e) {
        console.error("Gemini Goal Error:", e);
    }
    return null;
};

export const generateMissionBriefing = async (stats: CityStats): Promise<string | null> => {
     if (!ai) return null;
     try {
         const prompt = `Write a short 2-sentence mission briefing from the Board of Directors. 
         Situation: Compliance is at ${stats.compliance}%, Threat is ${stats.threat}%.`;
         
         const response = await ai.models.generateContent({
             model: COMPLEX_MODEL,
             contents: prompt,
             config: { systemInstruction: SYSTEM_INSTRUCTION }
         });
         return response.text || null;
     } catch (e) {
         console.error("Gemini Briefing Error", e);
     }
     return null;
};

export const generateSpeech = async (text: string): Promise<string | null> => {
    // Return null if AI is not available (no speech synthesis)
    if (!ai) {
        return null;
    }

    try {
        const response = await ai.models.generateContent({
            model: TTS_MODEL,
            contents: { parts: [{ text: text }] },
            config: {
                responseModalities: [Modality.AUDIO],
                speechConfig: {
                    voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } }
                }
            }
        });
        
        const audioData = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
        return audioData || null;
    } catch (e) {
        console.error("Gemini TTS Error", e);
    }
    return null;
};

export const generateScenarioBriefing = async (stats: CityStats): Promise<string | null> => {
    return generateMissionBriefing(stats);
};

export const generateCoachingTip = async (objective: string): Promise<string | null> => {
    if (!ai) return null;
    try {
        const prompt = `The player is stuck on this tutorial objective: "${objective}".
        Explain briefly (max 2 sentences) WHY this is important in a fintech/cybersecurity context. 
        Focus on the concept (e.g., why pipelines matter, why zone separation is key).`;

        const response = await ai.models.generateContent({
            model: FAST_MODEL,
            contents: prompt,
            config: { systemInstruction: SYSTEM_INSTRUCTION }
        });
        
        return response.text || null;
    } catch (e) {
        console.error("Gemini Coach Error", e);
        return "System offline. Consult manual.";
    }
};
